import type {RouteLocationNormalized, RouteRecordRaw} from 'vue-router';
import {createRouter, createWebHistory} from 'vue-router';

import {UserRoles} from '@/shared/enums/userRolesEnum';
import type {LoginMode} from '@/shared/features/tenants/tenant.model';
import {useAppErrorStore} from '@/shared/stores/appError.store';
import {useNavigationProgressStore} from '@/shared/stores/navigationProgress.store';
import {useTenantConfigStore} from '@/shared/stores/tenantConfig.store';
import {useUserStore} from '@/shared/stores/user.store';
import Error404View from '@/shared/views/Errors/Error404View.vue';

// A route chunk failing to fetch (server unreachable mid-session, dev server
// restarted, etc.) surfaces to router.onError with one of these messages
// across browsers — matched so it can be told apart from a genuine navigation
// error. Handled by flagging appError.loadFailed (see App.vue) rather than
// navigating to a dedicated route, so the URL/history stay put and a plain
// page reload retries the actual destination instead of a static error page.
const CHUNK_LOAD_ERROR_PATTERN = /dynamically imported module|importing a module script failed/i;

// Navigating to {name: 'NotFound'} with no params resolves the wildcard
// route's required `pathMatch` to empty, which collapses the URL to "/" —
// the browser ends up at the site root showing the 404 page instead of
// staying on the bad URL the user actually typed/followed. Passing the
// original path back in as `pathMatch` keeps the URL exactly where it was.
export function notFoundLocation(to: Pick<RouteLocationNormalized, 'path' | 'query' | 'hash'>) {
   return {
      name: 'NotFound',
      params: {pathMatch: to.path.split('/').filter(Boolean)},
      query: to.query,
      hash: to.hash,
   };
}

export function createTenantRouter(routes: RouteRecordRaw[]) {
   const router = createRouter({
      history: createWebHistory(),
      routes: [
         ...routes,
         {
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            component: Error404View,
         },
      ],
   });

   router.beforeEach((to) => {
      // Instant feedback that the click registered, even before the
      // destination's lazy chunk/data finishes loading — see
      // NavigationProgressBar.vue.
      useNavigationProgressStore().start();
      useAppErrorStore().clearLoadFailed();

      const requiredRoles = to.meta.requiredRoles as string[] | undefined;
      const userStore = useUserStore();
      const userRole = userStore.user?.role ?? null;

      if (requiredRoles && requiredRoles.length > 0) {
         const isAuthorized = userRole && requiredRoles.includes(userRole);
         if (!isAuthorized) {
            return notFoundLocation(to);
         }
      }

      // Sector-gated Metadata routes:
      // a route tagged with meta.loginMode is only reachable by direct navigation
      // (typed URL, bookmark, nav-hidden-but-not-router-guarded link) if the
      // caller's own tenant is in that sector. IT_ADMIN is exempt — they manage
      // every sector for every tenant, same as the server-side guard and the nav.
      const requiredLoginMode = to.meta.loginMode as LoginMode | undefined;
      if (requiredLoginMode && userRole !== UserRoles.IT_ADMIN) {
         const tenantConfigStore = useTenantConfigStore();
         if (tenantConfigStore.loginMode !== requiredLoginMode) {
            return notFoundLocation(to);
         }
      }
   });

   router.afterEach(() => {
      useNavigationProgressStore().finish();
   });

   router.onError((error) => {
      useNavigationProgressStore().finish();

      if (CHUNK_LOAD_ERROR_PATTERN.test(error?.message ?? '')) {
         useAppErrorStore().setLoadFailed();
      }
   });

   return router;
}
